<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <reports-table
    module="leave"
    name="subunit_leave_entitlements_and_usage"
    :filters="serializedFilters"
    :column-count="7"
  >
    <template #default="{generateReport}">
      <oxd-table-filter filter-title="Sub Unit Leave Report">
        <oxd-form @submit-valid="onGenerate(generateReport)">
          <oxd-form-row>
            <oxd-grid :cols="4" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.subunits"
                  type="multiselect"
                  :label="$t('general.sub_unit')"
                  :options="subunits"
                  :rules="rules.subunits"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <leave-period-dropdown
                  v-model="filters.leavePeriod"
                  :rules="rules.leavePeriod"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.leaveTypes"
                  type="multiselect"
                  :label="$t('leave.leave_type')"
                  :options="leaveTypeOptions"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.location"
                  type="select"
                  :label="$t('general.location')"
                  :options="locations"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <jobtitle-dropdown v-model="filters.jobTitle" />
              </oxd-grid-item>
              <oxd-grid-item class="orangehrm-leave-filter --span-column-2">
                <oxd-text class="orangehrm-leave-filter-text" tag="p">
                  {{ $t('leave.include_past_employees') }}
                </oxd-text>
                <oxd-switch-input v-model="filters.includePastEmps" />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <required-text />
            <oxd-button
              type="submit"
              display-type="secondary"
              class="orangehrm-left-space"
              :label="$t('general.generate')"
            />
            <oxd-button
              v-if="reportReady"
              type="button"
              display-type="ghost"
              class="orangehrm-left-space"
              label="Download CSV"
              @click="downloadExport('csv')"
            />
            <oxd-button
              v-if="reportReady"
              type="button"
              display-type="ghost"
              class="orangehrm-left-space"
              label="Download Excel"
              @click="downloadExport('excel')"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>
      <br />
    </template>
  </reports-table>
</template>

<script>
import {computed, onBeforeMount, ref} from 'vue';
import {required} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import ReportsTable from '@/core/components/table/ReportsTable';
import JobtitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown';
import LeavePeriodDropdown from '@/orangehrmLeavePlugin/components/LeavePeriodDropdown';
import {OxdSwitchInput} from '@ohrm/oxd';

const defaultFilters = {
  leavePeriod: null,
  leaveTypes: [],
  subunits: [],
  location: null,
  jobTitle: null,
  includePastEmps: false,
};

export default {
  components: {
    'reports-table': ReportsTable,
    'oxd-switch-input': OxdSwitchInput,
    'jobtitle-dropdown': JobtitleDropdown,
    'leave-period-dropdown': LeavePeriodDropdown,
  },
  props: {
    locations: {
      type: Array,
      default: () => [],
    },
    subunits: {
      type: Array,
      default: () => [],
    },
    leavePeriod: {
      type: Object,
      required: false,
      default: () => null,
    },
  },

  setup(props) {
    const filters = ref({
      ...defaultFilters,
      leaveTypes: [],
      subunits: [],
      ...(props.leavePeriod && {leavePeriod: props.leavePeriod}),
    });
    const leaveTypeOptions = ref([]);
    const reportReady = ref(false);
    const rules = ref({
      leavePeriod: [required],
      subunits: [required],
    });

    const serializedFilters = computed(() => {
      const selectedSubunits = Array.isArray(filters.value.subunits)
        ? filters.value.subunits
        : [];
      const selectedLeaveTypes = Array.isArray(filters.value.leaveTypes)
        ? filters.value.leaveTypes
        : [];

      return {
        name: 'subunit_leave_entitlements_and_usage',
        fromDate: filters.value.leavePeriod?.startDate,
        toDate: filters.value.leavePeriod?.endDate,
        subunitIds: selectedSubunits.map((item) => item.id),
        leaveTypeIds: selectedLeaveTypes.length
          ? selectedLeaveTypes.map((item) => item.id)
          : undefined,
        locationId: filters.value.location?.id,
        jobTitleId: filters.value.jobTitle?.id,
        includeEmployees: filters.value.includePastEmps
          ? 'currentAndPast'
          : 'onlyCurrent',
      };
    });

    const onGenerate = async (generateReport) => {
      await generateReport();
      reportReady.value = true;
    };

    const buildExportQuery = (format) => {
      const params = new URLSearchParams();
      params.append('format', format);
      const filtersValue = serializedFilters.value;
      if (filtersValue.fromDate) {
        params.append('fromDate', filtersValue.fromDate);
      }
      if (filtersValue.toDate) {
        params.append('toDate', filtersValue.toDate);
      }
      if (filtersValue.includeEmployees) {
        params.append('includeEmployees', filtersValue.includeEmployees);
      }
      if (filtersValue.locationId) {
        params.append('locationId', filtersValue.locationId);
      }
      if (filtersValue.jobTitleId) {
        params.append('jobTitleId', filtersValue.jobTitleId);
      }
      (filtersValue.subunitIds || []).forEach((id, index) => {
        params.append(`subunitIds[${index}]`, id);
      });
      (filtersValue.leaveTypeIds || []).forEach((id, index) => {
        params.append(`leaveTypeIds[${index}]`, id);
      });
      return params.toString();
    };

    const downloadExport = (format) => {
      const query = buildExportQuery(format);
      const downUrl = `${window.appGlobal.baseUrl}/leave/exportSubunitLeaveReport?${query}`;
      window.open(downUrl, '_blank');
    };

    onBeforeMount(() => {
      const http = new APIService(
        window.appGlobal.baseUrl,
        '/api/v2/leave/leave-types',
      );
      http.getAll({limit: 0}).then(({data}) => {
        leaveTypeOptions.value = data.data.map((item) => {
          return {
            id: item.id,
            label: item.name,
            isDeleted: item.deleted,
          };
        });
      });
    });

    return {
      rules,
      filters,
      reportReady,
      leaveTypeOptions,
      serializedFilters,
      onGenerate,
      downloadExport,
    };
  },
};
</script>

<style src="./leave-entitlement-report.scss" lang="scss" scoped></style>
